import { Component, inject, signal, viewChild } from '@angular/core';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { ClientHoursComponent } from './client-hours/client-hours.component';
import { NotificationManagementComponent } from './notification-management/notification-management.component';
import { IfHasPermissionDirective } from 'src/app/shared/directives/if-has-permission.directive';
import { DialogComponent } from 'src/app/shared/dialogs/dialog/dialog.component';
import { DIALOG_BUTTONS } from 'src/app/shared/constants';

@Component({
  selector: 'app-logged-time',
  imports: [
    MatTabGroup,
    MatTab,
    ClientHoursComponent,
    NotificationManagementComponent,
    TranslateModule,
    IfHasPermissionDirective,
  ],
  templateUrl: './logged-time.component.html',
  styleUrl: './logged-time.component.scss',
})
export class LoggedTimeComponent {
  private readonly TABS = ['logged-time', 'notifications'];

  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationManagement = viewChild(NotificationManagementComponent);

  showNotificationManagement = signal(true);
  private reverting = false;

  activeTabIndex = toSignal(
    this.route.queryParams.pipe(
      map(params => {
        const index = this.TABS.indexOf(params['tab']);
        return index >= 0 ? index : 0;
      }),
    ),
    { initialValue: 0 },
  );

  onTabChange(newIndex: number): void {
    if (this.reverting) {
      this.reverting = false;
      return;
    }

    const prevIndex = this.activeTabIndex();

    if (prevIndex !== 0 && this.notificationManagement()?.hasPendingChanges()) {
      this.reverting = true;
      this.navigate(prevIndex);
      this.dialog
        .open(DialogComponent, {
          data: {
            title: this.translate.instant('ADMIN.DAYS_LEFT.UNSAVED_CHANGES_TITLE'),
            description: this.translate.instant('ADMIN.DAYS_LEFT.UNSAVED_CHANGES_DESCRIPTION'),
            sharedButtonClass: DIALOG_BUTTONS.warningButton,
            sharedButtonText: this.translate.instant('ADMIN.DAYS_LEFT.UNSAVED_CHANGES_CONFIRM'),
          },
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) {
            this.navigate(newIndex);
            this.showNotificationManagement.set(false);
            setTimeout(() => this.showNotificationManagement.set(true));
          }
        });
    } else {
      this.navigate(newIndex);
    }
  }

  private navigate(tabIndex: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.TABS[tabIndex] },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
